import { AppDenyEntry } from '@/types'

export const MAC_BUNDLE_ID_PATTERN = /^[a-z0-9](?:[a-z0-9-]*[a-z0-9])?(?:\.[a-z0-9](?:[a-z0-9-]*[a-z0-9])?)+$/
export const WIN_PROCESS_NAME_PATTERN = /^[^<>:"/\\|?*\u0000-\u001f]+$/

export function canonicalizeIdentifier(raw: string | null | undefined): string {
  return (raw ?? '').trim().toLowerCase()
}

export function canonicalizeMacBundleId(raw: string | null | undefined): string {
  return canonicalizeIdentifier(raw)
}

export function canonicalizeWinProcessName(raw: string | null | undefined): string {
  const canonical = canonicalizeIdentifier(raw)
  return canonical.endsWith('.exe') ? canonical.slice(0, -'.exe'.length) : canonical
}

export interface EntryDraft {
  displayName: string
  macBundleId: string
  winProcessName: string
}

export type ValidatedDraft = { entry: AppDenyEntry; error: null } | { entry?: undefined; error: string }

export function validateDraft(draft: EntryDraft): ValidatedDraft {
  const displayName = draft.displayName.trim()
  const rawMacBundleId = draft.macBundleId.trim()
  const rawWinProcessName = draft.winProcessName.trim()
  const macBundleId = canonicalizeMacBundleId(draft.macBundleId)
  const winProcessName = canonicalizeWinProcessName(draft.winProcessName)

  if (!displayName) {
    return { error: 'Enter a display name.' }
  }
  if (!rawMacBundleId && !rawWinProcessName) {
    return { error: 'Add a macOS bundle ID or a Windows process name.' }
  }
  if (rawMacBundleId && !MAC_BUNDLE_ID_PATTERN.test(macBundleId)) {
    return { error: 'macOS bundle ID must be a reverse-DNS identifier with letters, numbers, dots, and dashes.' }
  }
  if (rawWinProcessName && (!winProcessName || !WIN_PROCESS_NAME_PATTERN.test(winProcessName))) {
    return { error: 'Windows process name must be an executable name without path separators or reserved characters.' }
  }

  if (macBundleId && winProcessName) {
    return { entry: { displayName, macBundleId, winProcessName }, error: null }
  }
  if (macBundleId) {
    return { entry: { displayName, macBundleId }, error: null }
  }
  if (winProcessName) {
    return { entry: { displayName, winProcessName }, error: null }
  }

  return { error: 'Add a macOS bundle ID or a Windows process name.' }
}

export type PlatformCoverage = 'macOS' | 'Windows' | 'macOS + Windows' | 'Unknown'

export function platformCoverage(entry: AppDenyEntry): PlatformCoverage {
  const hasMac = Boolean(canonicalizeMacBundleId(entry.macBundleId))
  const hasWin = Boolean(canonicalizeWinProcessName(entry.winProcessName))
  if (hasMac && hasWin) return 'macOS + Windows'
  if (hasMac) return 'macOS'
  if (hasWin) return 'Windows'
  return 'Unknown'
}
